// Premium customer authentication modal with Personal / Business tabs.
//
// MMT-inspired UX (not cloned): tabbed top, mobile/email/Google methods per tab,
// OTP entry with auto-focus + timer + resend, Google one-click, animated drawer
// on mobile, modal on desktop. Mobile-first responsive throughout.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { X, Mail, Phone, Briefcase, ShieldCheck, ArrowRight, Loader2, RefreshCw } from 'lucide-react';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { formatApiErrorDetail } from '../../lib/api';

const COUNTRY_CODES = [
  { code: '+91',  flag: '🇮🇳', label: 'IN' },
  { code: '+1',   flag: '🇺🇸', label: 'US' },
  { code: '+44',  flag: '🇬🇧', label: 'UK' },
  { code: '+971', flag: '🇦🇪', label: 'AE' },
  { code: '+65',  flag: '🇸🇬', label: 'SG' },
  { code: '+61',  flag: '🇦🇺', label: 'AU' },
];

// Inputs
const inputCls = 'w-full h-12 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:outline-none placeholder:text-gray-400';

// ─── OTP six-box input with autofocus chain ──────────────────────────
const OTPInput = ({ value, onChange, length = 6, autoFocus = true }) => {
  const refs = useRef([]);
  useEffect(() => { if (autoFocus) refs.current[0]?.focus(); }, [autoFocus]);

  const onKey = (i) => (e) => {
    if (e.key === 'Backspace' && !value[i] && i > 0) refs.current[i - 1]?.focus();
  };
  const onCh = (i) => (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(-1);
    const next = (value || '').split('');
    while (next.length < length) next.push('');
    next[i] = v;
    onChange(next.join('').slice(0, length));
    if (v && i < length - 1) refs.current[i + 1]?.focus();
  };
  const onPaste = (e) => {
    const txt = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, length);
    if (txt) { onChange(txt); refs.current[Math.min(txt.length, length - 1)]?.focus(); e.preventDefault(); }
  };

  return (
    <div className="flex gap-2 justify-between" onPaste={onPaste} data-testid="otp-input">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          inputMode="numeric"
          maxLength={1}
          value={(value || '')[i] || ''}
          onChange={onCh(i)}
          onKeyDown={onKey(i)}
          aria-label={`OTP digit ${i + 1}`}
          className="w-11 h-12 sm:w-12 sm:h-14 text-center text-lg font-bold rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:outline-none tabular-nums"
          data-testid={`otp-input-${i}`}
        />
      ))}
    </div>
  );
};

// ─── Google sign-in button ───────────────────────────────────────────
const GoogleButton = ({ accountType, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`auth-google-${accountType}`}
    className="w-full h-12 inline-flex items-center justify-center gap-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 tap-target"
  >
    <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.2 29.2 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.2-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.3 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.2 29.2 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.1 0 9.8-2 13.3-5.1l-6.1-5c-2 1.4-4.5 2.1-7.2 2.1-5.2 0-9.7-3.4-11.3-8l-6.5 5C9.7 39.7 16.3 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.8-3.7 5.1l6.1 5c-.4.4 7.3-5.3 7.3-14.1 0-1.2-.1-2.3-.4-3.5z" />
    </svg>
    Continue with Google
  </button>
);

// ─── Tab pane: PERSONAL ──────────────────────────────────────────────
const PersonalPane = ({ onClose }) => {
  const [mode, setMode] = useState('mobile'); // mobile | email
  return (
    <div className="space-y-4">
      <div className="flex border-b border-gray-200 dark:border-gray-800">
        {[
          { v: 'mobile', label: 'Mobile', Icon: Phone },
          { v: 'email', label: 'Email', Icon: Mail },
        ].map((m) => (
          <button
            key={m.v}
            onClick={() => setMode(m.v)}
            data-testid={`auth-method-${m.v}`}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 py-3 text-sm font-semibold tap-target ${mode === m.v ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-500'}`}
          >
            <m.Icon className="h-4 w-4" /> {m.label}
          </button>
        ))}
      </div>
      {mode === 'mobile' && <MobileForm onClose={onClose} />}
      {mode === 'email' && <EmailForm onClose={onClose} accountType="personal" />}
      <div className="relative my-3">
        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200 dark:border-gray-800" /></div>
        <div className="relative flex justify-center"><span className="bg-white dark:bg-gray-900 px-3 text-[11px] uppercase tracking-wider text-gray-400">or</span></div>
      </div>
      <GoogleButton accountType="personal" onClick={() => startGoogleLogin('personal')} />
    </div>
  );
};

// ─── Tab pane: BUSINESS ──────────────────────────────────────────────
const BusinessPane = ({ onClose }) => (
  <div className="space-y-4">
    <div className="rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 p-3 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
      <Briefcase className="h-4 w-4 mt-0.5 shrink-0" />
      <span>Use your <strong>work email</strong> — get GST-compliant invoicing, corporate fares and centralised billing.</span>
    </div>
    <EmailForm onClose={onClose} accountType="corporate" workEmailOnly />
    <div className="relative my-3">
      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200 dark:border-gray-800" /></div>
      <div className="relative flex justify-center"><span className="bg-white dark:bg-gray-900 px-3 text-[11px] uppercase tracking-wider text-gray-400">or</span></div>
    </div>
    <GoogleButton accountType="business" onClick={() => startGoogleLogin('business')} />
  </div>
);

// ─── Mobile OTP form ─────────────────────────────────────────────────
const MobileForm = ({ onClose }) => {
  const { sendOtp, verifyOtp } = useCustomerAuth();
  const [step, setStep] = useState(1); // 1 = enter number, 2 = enter OTP
  const [country, setCountry] = useState('+91');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [demoOtp, setDemoOtp] = useState(null);

  // Resend timer
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const send = async () => {
    if (mobile.replace(/\D/g, '').length < 6) { toast.error('Enter a valid mobile number'); return; }
    setSending(true);
    try {
      const r = await sendOtp({ mobile, country_code: country });
      setStep(2);
      setResendIn(r.resend_in_seconds || 30);
      if (r.demo_mode) {
        setDemoOtp(r.dev_otp);
        toast.success(`Demo OTP: ${r.dev_otp}`, { duration: 7000 });
      } else {
        toast.success(`OTP sent to ${country} ${mobile}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to send OTP');
    } finally { setSending(false); }
  };

  const verify = async () => {
    if (otp.length < 4) { toast.error('Enter the OTP'); return; }
    setVerifying(true);
    try {
      const user = await verifyOtp({ mobile, country_code: country, otp });
      toast.success(`Welcome${user?.name ? `, ${user.name}` : ''}!`);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'Incorrect OTP');
    } finally { setVerifying(false); }
  };

  if (step === 1) {
    return (
      <div className="space-y-3">
        <div className="flex gap-2">
          <select value={country} onChange={(e) => setCountry(e.target.value)} className={`${inputCls} w-24 sm:w-28`} data-testid="auth-country-code">
            {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{`${c.flag} ${c.code}`}</option>)}
          </select>
          <input
            type="tel"
            inputMode="numeric"
            value={mobile}
            onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 12))}
            placeholder="Enter mobile number"
            className={inputCls}
            data-testid="auth-mobile-input"
          />
        </div>
        <button onClick={send} disabled={sending || mobile.length < 6}
                data-testid="auth-mobile-send"
                className="w-full h-12 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold text-sm tap-target inline-flex items-center justify-center gap-2">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          {sending ? 'Sending OTP…' : 'Continue'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        We've sent a 6-digit code to <strong>{country} {mobile}</strong>
      </p>
      <OTPInput value={otp} onChange={setOtp} />
      {demoOtp && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
          Demo mode · your code is <strong className="tabular-nums">{demoOtp}</strong>
        </p>
      )}
      <button onClick={verify} disabled={verifying || otp.length < 4}
              data-testid="auth-otp-verify"
              className="w-full h-12 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold text-sm tap-target inline-flex items-center justify-center gap-2">
        {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
        {verifying ? 'Verifying…' : 'Verify & Sign in'}
      </button>
      <div className="flex items-center justify-between text-xs">
        <button onClick={() => setStep(1)} className="text-gray-500 hover:text-gray-700" data-testid="auth-otp-back">Change number</button>
        <button onClick={send} disabled={resendIn > 0 || sending} data-testid="auth-otp-resend"
                className="inline-flex items-center gap-1 text-red-600 disabled:text-gray-400">
          <RefreshCw className="h-3 w-3" /> {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend OTP'}
        </button>
      </div>
    </div>
  );
};

// ─── Email form (signup OR login) ────────────────────────────────────
const EmailForm = ({ onClose, accountType = 'personal', workEmailOnly = false }) => {
  const { loginWithEmail, signupWithEmail } = useCustomerAuth();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const PERSONAL = useMemo(() => new Set(['gmail.com', 'yahoo.com', 'yahoo.co.in', 'outlook.com', 'hotmail.com', 'icloud.com', 'rediffmail.com', 'protonmail.com']), []);
  const isPersonalDomain = (e) => PERSONAL.has((e.split('@')[1] || '').toLowerCase());

  const submit = async () => {
    if (!email || !password) { toast.error('Email and password required'); return; }
    if (workEmailOnly && isPersonalDomain(email)) {
      toast.error('Please use your work email (not gmail/yahoo etc.)');
      return;
    }
    setBusy(true);
    try {
      const user = mode === 'login'
        ? await loginWithEmail({ email, password })
        : await signupWithEmail({ email, password, name, account_type: accountType });
      toast.success(`Welcome${user?.name ? `, ${user.name}` : ''}!`);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'Sign-in failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 text-xs">
        {['login', 'signup'].map((m) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`auth-email-mode-${m}`}
                  className={`flex-1 py-1.5 rounded-lg font-semibold uppercase tracking-wider tap-target ${mode === m ? 'bg-red-50 text-red-700' : 'text-gray-500'}`}>
            {m === 'login' ? 'Log in' : 'Sign up'}
          </button>
        ))}
      </div>
      {mode === 'signup' && (
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputCls} data-testid="auth-email-name" />
      )}
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={workEmailOnly ? 'you@yourcompany.com' : 'you@example.com'} className={inputCls} data-testid="auth-email-input" />
      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} data-testid="auth-email-password" />
      <button onClick={submit} disabled={busy} data-testid="auth-email-submit"
              className="w-full h-12 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold text-sm tap-target inline-flex items-center justify-center gap-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
        {busy ? 'Please wait…' : mode === 'login' ? 'Log in' : 'Create account'}
      </button>
    </div>
  );
};

// ─── BYO Google OAuth — direct browser flow via Google Identity Services ─
// Uses the GSI script loaded in public/index.html. Pops Google's own dialog
// directly, requests an ID token, then POSTs to the backend.
function startGoogleLogin(accountType) {
  const clientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
  if (!clientId) {
    // eslint-disable-next-line no-alert
    alert('Google login is not configured (REACT_APP_GOOGLE_OAUTH_CLIENT_ID missing).');
    return;
  }
  if (!window.google || !window.google.accounts || !window.google.accounts.id) {
    // eslint-disable-next-line no-alert
    alert('Google Identity script is still loading — please try again in a second.');
    return;
  }
  // Stash account-type so the callback page can apply it after login.
  try { sessionStorage.setItem('pending_google_ctx', accountType); } catch (e) { /* ignore */ }

  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: (resp) => {
      if (!resp || !resp.credential) return;
      // Hand off to the existing callback page which performs the JWT exchange.
      const url = `${window.location.origin}/auth/google/callback?ctx=${accountType}#id_token=${encodeURIComponent(resp.credential)}`;
      window.location.href = url;
    },
    ux_mode: 'popup',
    auto_select: false,
    cancel_on_tap_outside: true,
  });
  // Trigger the prompt + One-Tap fallback chain.
  window.google.accounts.id.prompt();
}

// ─── Modal shell ─────────────────────────────────────────────────────
const AuthModal = ({ open, onClose, defaultTab = 'personal' }) => {
  const [tab, setTab] = useState(defaultTab);

  useEffect(() => { if (open) setTab(defaultTab); }, [open, defaultTab]);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-stretch sm:items-center justify-center" data-testid="auth-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-900 w-full sm:max-w-md sm:rounded-2xl sm:shadow-2xl max-h-[100vh] sm:max-h-[92vh] overflow-y-auto sm:mx-4 flex flex-col">
        {/* Header */}
        <div className="relative px-5 pt-5 pb-3">
          <button onClick={onClose} aria-label="Close" data-testid="auth-modal-close" className="absolute right-3 top-3 tap-target p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Welcome to Elite Travo</h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Get exclusive deals, faster checkout & booking history.</p>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-2 mx-5 rounded-lg bg-gray-100 dark:bg-gray-800 p-1 mb-4">
          <button onClick={() => setTab('personal')} data-testid="auth-tab-personal"
                  className={`py-2 rounded-md text-xs sm:text-sm font-bold uppercase tracking-wider tap-target ${tab === 'personal' ? 'bg-white dark:bg-gray-700 shadow text-red-600' : 'text-gray-600 dark:text-gray-400'}`}>
            Personal
          </button>
          <button onClick={() => setTab('business')} data-testid="auth-tab-business"
                  className={`py-2 rounded-md text-xs sm:text-sm font-bold uppercase tracking-wider tap-target ${tab === 'business' ? 'bg-white dark:bg-gray-700 shadow text-red-700' : 'text-gray-600 dark:text-gray-400'}`}>
            Business
          </button>
        </div>

        <div className="px-5 pb-5">
          {tab === 'personal' ? <PersonalPane onClose={onClose} /> : <BusinessPane onClose={onClose} />}
          <p className="text-[11px] text-gray-400 text-center mt-4 leading-relaxed">
            By continuing you agree to our <a className="underline">Terms</a> and <a className="underline">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
